import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Mail, Shield, Users, Heart } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import getCanonicalUrl from '../utils/canonical';

const Roma = () => {

  return (
    <div className="page-container">
      <Helmet>
        <title>Agenzia Matrimoniale Roma | Officine del Cuore Incontri per Single</title>
        <meta name="description" content="Contatta Agenzia Matrimoniale Roma Officine del Cuore: incontri per relazione seria, iscritti verificati, massima riservatezza, presenti in tutto il Lazio." />
        <link rel="canonical" href={getCanonicalUrl('agenzia-matrimoniale-roma')} />
      </Helmet>

      {/* Hero Section */}
      <section className="page-hero" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_officine-cuore/artifacts/yy88q85a_coppia%20colosseo2w.webp)'}}>
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="page-title fade-in">Agenzia Matrimoniale Roma</h1>
          <p className="page-subtitle fade-in-delay">
            Officine del Cuore - Incontri Seri e Relazioni Durature
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="section-elegant">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="section-title text-center mb-8">
            La Tua Agenzia Matrimoniale a Roma
          </h2>
          <div className="prose-elegant">
            <p>
              Officine del Cuore è la tua agenzia matrimoniale di riferimento a Roma. Con oltre 19 anni di esperienza, offriamo un servizio personalizzato e riservato per single esigenti che cercano una relazione seria e duratura nella Capitale.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-elegant bg-warm-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="section-title text-center mb-12">
            I Nostri Servizi a Roma
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="benefit-card">
              <CardContent className="p-6 text-center">
                <div className="benefit-icon-wrapper">
                  <Shield className="benefit-icon" />
                </div>
                <h3 className="benefit-title">Profili Verificati</h3>
                <p className="benefit-description">
                  Ogni iscritto è verificato e certificato dal nostro team.
                </p>
              </CardContent>
            </Card>

            <Card className="benefit-card">
              <CardContent className="p-6 text-center">
                <div className="benefit-icon-wrapper">
                  <Users className="benefit-icon" />
                </div>
                <h3 className="benefit-title">Incontri Selezionati</h3>
                <p className="benefit-description">
                  Presentazioni mirate basate su compatibilità reale, non algoritmi.
                </p>
              </CardContent>
            </Card>

            <Card className="benefit-card">
              <CardContent className="p-6 text-center">
                <div className="benefit-icon-wrapper">
                  <Heart className="benefit-icon" />
                </div>
                <h3 className="benefit-title">Supporto Umano</h3>
                <p className="benefit-description">
                  Una persona vera ti ascolta, ti comprende e ti guida durante tutto il percorso.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - 4 Info Boxes */}
      <section className="section-elegant">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Box 1 */}
            <Card className="info-card">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4" style={{color: 'var(--burgundy)'}}>
                  Perché scegliere Officine del Cuore a Roma
                </h3>
                <p className="leading-relaxed" style={{color: 'var(--burgundy)'}}>
                  A Roma, come in tutta Italia, le app di dating offrono migliaia di profili ma pochi risultati concreti. Officine del Cuore fa la differenza: ogni iscritto è verificato personalmente, ogni presentazione è studiata sulla base di compatibilità reale. Non algoritmi, ma persone che ti ascoltano e ti guidano e massima riservatezza assicurata.
                </p>
              </CardContent>
            </Card>

            {/* Box 2 */}
            <Card className="info-card">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4" style={{color: 'var(--burgundy)'}}>
                  Come funziona il nostro servizio a Roma
                </h3>
                <p className="leading-relaxed" style={{color: 'var(--burgundy)'}}>
                  Puoi iscriverti comodamente da casa in videochiamata o visitarci di persona. Il nostro team ti seguirà in ogni fase: dalla conoscenza iniziale alla selezione dei profili più compatibili, fino al primo incontro e oltre. Garantiamo la massima riservatezza e un supporto continuo.
                </p>
              </CardContent>
            </Card>

            {/* Box 3 */}
            <Card className="info-card">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4" style={{color: 'var(--burgundy)'}}>
                  Incontri seri per single romani
                </h3>
                <p className="leading-relaxed" style={{color: 'var(--burgundy)'}}>
                  Che tu viva ai Parioli, a Trastevere, all'EUR o in qualsiasi zona di Roma e del Lazio, il nostro servizio è pensato per chi non vuole perdere tempo. Profili certificati, nessun fake, solo persone reali e motivate a costruire qualcosa di importante.
                </p>
              </CardContent>
            </Card>

            {/* Box 4 */}
            <Card className="info-card">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4" style={{color: 'var(--burgundy)'}}>
                  Modalità di accesso ai nostri servizi
                </h3>
                <p className="leading-relaxed" style={{color: 'var(--burgundy)'}}>
                  Puoi venire in sede oppure optare per un colloquio approfondito telefonico e in videochiamata, che puoi svolgere comodamente da casa tua. L'iscrizione a distanza permette di avvicinarsi alla nostra agenzia in modo discreto e naturale. Questo approccio mantiene intatta la qualità del servizio e consente anche un risparmio economico, grazie a minori costi organizzativi e a una gestione più snella. Da noi non paghi ciò che non serve. Puoi iniziare nel luogo in cui ti senti più tranquillo e a tuo agio: casa tua. Perché non è il luogo che conta, ma l'attenzione che ricevi. In ogni caso ci sarà la verifica con la visione e l'invio dei documenti a mezzo whatsapp o email.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Info Roma */}
      <section className="section-elegant bg-warm-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="section-title text-center mb-12">
            Contattaci a Roma
          </h2>
          <Card className="contact-card">
            <CardContent className="p-8">
              <div className="contact-info-list">
                <div className="contact-info-item">
                  <MapPin className="text-gold" size={24} />
                  <div>
                    <p className="font-medium">Via Mattia Battistini, 466a</p>
                    <p className="text-sm">Roma</p>
                  </div>
                </div>
                <div className="contact-info-item">
                  <Phone className="text-gold" size={24} />
                  <a href="tel:+393455006556" className="hover:text-gold transition-colors">
                    Telefono/WhatsApp: +39 3455006556
                  </a>
                </div>
                <div className="contact-info-item">
                  <a 
                    href="https://www.google.com/search?q=agenzia+per+single+officine+del+cuore+roma+terzo+spazio&oq=agenzia+per+single+officine+del+cuore+roma+terzo+spazio&gs_lcrp=EgZjaHJvbWUyBggAEEUYOTIGCAEQRRg90gEJMTU1OTFqMGo3qAIAsAIA&sourceid=chrome&ie=UTF-8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_officine-cuore/artifacts/vv5zqm3p_google%20icona.png"
                      alt="Recensioni Google"
                      className="w-8 h-8"
                    />
                    <span className="hover:text-gold transition-colors">Guardaci su Google</span>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section-alt">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="cta-title">
            Richiedi un Colloquio Riservato
          </h2>
          <p className="cta-description">
            Contattaci per un colloquio conoscitivo gratuito. 
            Scopri come possiamo aiutarti a trovare la persona giusta per te a Roma.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
            <Link to="/contatti">
              <Button size="lg" className="btn-primary btn-large">
                Contattaci Ora
              </Button>
            </Link>
            <a href="tel:+393455006556">
              <Button size="lg" className="btn-primary btn-large">
                Chiama Ora
              </Button>
            </a>
            <a href="https://wa.me/393455006556" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="btn-primary btn-large">
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Roma;
